/**
 * A10 — Onboarding Step 1/4: Datos fiscales
 *
 * Pre-loads workspace_name from context (set during CreateWorkspace A05).
 * On submit: UPDATE empresa table with fiscal data via Supabase.
 */

import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, CheckCircle } from 'lucide-react';
import { Input } from '@shared/ui/shadcn/ui/input';
import { Button } from '@shared/ui/shadcn/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/ui/shadcn/ui/select';
import { Alert, AlertDescription } from '@shared/ui/shadcn/ui/alert';
import { supabase } from '@shared/lib/supabaseClient';
import OnboardingLayout from '../../components/OnboardingLayout';
import { useOnboarding } from '../../contexts/OnboardingContext';

const FiscalPage: React.FC = () => {
  const navigate = useNavigate();
  const { state, setFiscal } = useOnboarding();
  const { fiscal, empresaId, workspaceName } = state;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // ── Logo upload handler ──
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('El archivo excede 5 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setFiscal({ logoFile: file, logoPreview: reader.result as string });
    };
    reader.readAsDataURL(file);
  };

  // ── Submit ──
  const handleNext = async () => {
    setError(null);
    setLoading(true);

    try {
      // Upload logo if selected
      let logoUrl: string | null = null;
      if (fiscal.logoFile && empresaId) {
        const ext = fiscal.logoFile.name.split('.').pop() || 'png';
        const path = `empresas/${empresaId}/logo.${ext}`;
        const { error: uploadErr } = await supabase.storage
          .from('empresa-assets')
          .upload(path, fiscal.logoFile, { upsert: true });
        if (!uploadErr) {
          const { data: urlData } = supabase.storage
            .from('empresa-assets')
            .getPublicUrl(path);
          logoUrl = urlData?.publicUrl ?? null;
        }
      }

      // Update empresa table
      if (empresaId) {
        const updatePayload: Record<string, any> = {
          razon_social: fiscal.razonSocial || workspaceName,
          rnc: fiscal.rnc || null,
          tipo_contribuyente: fiscal.tipoContribuyente,
          direccion: fiscal.direccion || null,
          ciudad: fiscal.ciudad || null,
          provincia: fiscal.provincia || null,
          codigo_postal: fiscal.codigoPostal || null,
          telefono_comercial: fiscal.telefono || null,
        };
        if (logoUrl) updatePayload.logo_url = logoUrl;

        const { error: dbErr } = await (supabase as any)
          .from('empresas')
          .update(updatePayload)
          .eq('id', empresaId);

        if (dbErr) {
          console.error('[Fiscal] DB update error:', dbErr);
          setError('Error al guardar datos fiscales. Intenta de nuevo.');
          setLoading(false);
          return;
        }
      }

      navigate('/onboarding/equipo');
    } catch (err: any) {
      setError(err?.message || 'Error inesperado.');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => navigate('/onboarding/equipo');

  return (
    <OnboardingLayout
      step={1}
      onNext={handleNext}
      nextLabel="Continuar a equipo"
      loading={loading}
      skipLabel="Completar despues"
      onSkip={handleSkip}
    >
      <h1 className="text-3xl font-bold tracking-tight">Confirma los datos fiscales.</h1>
      <p className="text-[15px] text-muted-foreground leading-relaxed max-w-[56ch] mb-6">
        Apareceran en cotizaciones y facturas que envies a tus clientes. Los puedes actualizar despues en Settings.
      </p>

      {/* Pre-load banner */}
      {workspaceName && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-md px-3.5 py-2.5 text-[12.5px] text-green-700 mb-1">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          <span>Pre-cargamos el nombre que ingresaste al crear el workspace. Confirma o ajusta lo que falte.</span>
        </div>
      )}

      {error && (
        <Alert variant="destructive" className="mb-1">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col gap-3.5" style={{ maxWidth: 'none' }}>
        {/* Logo */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium flex justify-between items-baseline">
            Logo de la empresa
            <span className="font-mono text-[11px] text-muted-foreground font-normal">PNG o SVG - 512x512 ideal</span>
          </label>
          <div className="flex items-center gap-3.5">
            <div
              className="w-[72px] h-[72px] border-[1.5px] border-dashed border-border rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden"
              onClick={() => fileRef.current?.click()}
              style={{ cursor: 'pointer' }}
            >
              {fiscal.logoPreview ? (
                <img src={fiscal.logoPreview} alt="Logo" className="w-full h-full object-cover" />
              ) : (
                <span className="font-mono text-[11px] text-muted-foreground text-center leading-tight">
                  arrastra<br />logo
                </span>
              )}
            </div>
            <div>
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => fileRef.current?.click()}
              >
                <Upload className="w-3.5 h-3.5 mr-1.5" />
                Subir imagen
              </Button>
              <p className="text-[11.5px] text-muted-foreground mt-1.5">Acepta JPG, PNG, SVG hasta 5 MB</p>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml"
              onChange={handleLogoChange}
              className="hidden"
            />
          </div>
        </div>

        {/* Razon social */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium flex justify-between items-baseline">
            Razon social
            {workspaceName && (
              <span className="font-mono text-[11px] text-green-600 font-normal flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> pre-cargado desde tu workspace
              </span>
            )}
          </label>
          <Input
            value={fiscal.razonSocial}
            onChange={(e) => setFiscal({ razonSocial: e.target.value })}
            className={workspaceName ? 'border-green-300 bg-green-50/50' : ''}
          />
        </div>

        {/* RNC + Tipo contribuyente */}
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium">RNC</label>
            <Input
              placeholder="000-00000-0"
              value={fiscal.rnc}
              onChange={(e) => setFiscal({ rnc: e.target.value })}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium">Tipo de contribuyente</label>
            <Select
              value={fiscal.tipoContribuyente}
              onValueChange={(v) => setFiscal({ tipoContribuyente: v as 'persona-juridica' | 'persona-fisica' })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="persona-juridica">Persona juridica</SelectItem>
                <SelectItem value="persona-fisica">Persona fisica</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Direccion */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium">Direccion fiscal</label>
          <Input
            placeholder="Calle, numero, sector"
            value={fiscal.direccion}
            onChange={(e) => setFiscal({ direccion: e.target.value })}
          />
        </div>

        {/* Ciudad / Provincia / Codigo postal */}
        <div className="grid grid-cols-3 gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium">Ciudad</label>
            <Input
              value={fiscal.ciudad}
              onChange={(e) => setFiscal({ ciudad: e.target.value })}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium">Provincia / Estado</label>
            <Input
              value={fiscal.provincia}
              onChange={(e) => setFiscal({ provincia: e.target.value })}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium">Codigo postal</label>
            <Input
              value={fiscal.codigoPostal}
              onChange={(e) => setFiscal({ codigoPostal: e.target.value })}
            />
          </div>
        </div>

        {/* Telefono */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium flex justify-between items-baseline">
            Telefono de contacto comercial
            <span className="font-mono text-[11px] text-muted-foreground font-normal">opcional - aparece en cotizaciones</span>
          </label>
          <Input
            placeholder="+1 809 ..."
            value={fiscal.telefono}
            onChange={(e) => setFiscal({ telefono: e.target.value })}
          />
        </div>
      </div>
    </OnboardingLayout>
  );
};

export default FiscalPage;
